import React from 'react';
import { Card } from '../ui/Card';
import { Users, AlertTriangle, AlertCircle, CheckCircle2, TrendingUp, TrendingDown, MapPinOff } from 'lucide-react';
import { cn } from '../../lib/utils';
export type KpiFilter = 'all' | 'queueStuck' | 'missingCampaign' | 'enrolled' | 'unlicensed';
interface KpiData {
  totalLeads: number;
  queueStuck: number;
  missingCampaign: number;
  enrolled: number;
  unlicensed: number;
}
interface KpiCardsProps {
  data: KpiData;
  activeFilter: KpiFilter;
  onSelect: (filter: KpiFilter) => void;
}
interface TileProps {
  filterKey: KpiFilter;
  active: boolean;
  onSelect: (filter: KpiFilter) => void;
  borderClass: string;
  bgClass: string;
  ringClass: string;
  children: React.ReactNode;
}
function Tile({
  filterKey,
  active,
  onSelect,
  borderClass,
  bgClass,
  ringClass,
  children
}: TileProps) {
  return <Card role="button" tabIndex={0} aria-pressed={active} onClick={() => onSelect(active && filterKey !== 'all' ? 'all' : filterKey)} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(active && filterKey !== 'all' ? 'all' : filterKey);
    }
  }} className={cn('p-5 cursor-pointer group relative overflow-hidden transition-all duration-150', 'hover:shadow-md hover:-translate-y-0.5', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2', borderClass, bgClass, active ? `ring-2 ring-offset-2 shadow-md ${ringClass}` : '')} data-id="element-115">
      {children}
    </Card>;
}
export function KpiCards({
  data,
  activeFilter,
  onSelect
}: KpiCardsProps) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6" data-id="element-116">
      {/* Total Leads */}
      <Tile filterKey="all" active={activeFilter === 'all'} onSelect={onSelect} borderClass="border-slate-200" bgClass="bg-white" ringClass="ring-indigo-500 focus-visible:ring-indigo-500" data-id="element-117">
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-118">
          <Users className="w-16 h-16 text-slate-900" data-id="element-119" />
        </div>
        <div className="relative z-10" data-id="element-120">
          <div className="flex items-center justify-between mb-2" data-id="element-121">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-122">
              Total Leads
            </h3>
            <div className="flex items-center text-emerald-600 text-xs font-medium bg-emerald-50 px-2 py-1 rounded-full" data-id="element-123">
              <TrendingUp className="w-3 h-3 mr-1" data-id="element-124" />
              12%
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-125">
            {data.totalLeads}
          </div>
          <p className="text-xs text-slate-500" data-id="element-126">
            Created during selected timeframe
          </p>
        </div>
      </Tile>

      {/* Queue-Stuck */}
      <Tile filterKey="queueStuck" active={activeFilter === 'queueStuck'} onSelect={onSelect} borderClass="border-amber-200" bgClass="bg-amber-50/30" ringClass="ring-amber-500 focus-visible:ring-amber-500" data-id="element-127">
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-128">
          <AlertTriangle className="w-16 h-16 text-amber-600" data-id="element-129" />
        </div>
        <div className="relative z-10" data-id="element-130">
          <div className="flex items-center justify-between mb-2" data-id="element-131">
            <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider" data-id="element-132">
              Queue-Stuck
            </h3>
            <div className="flex items-center text-rose-600 text-xs font-medium bg-rose-50 px-2 py-1 rounded-full" data-id="element-133">
              <TrendingUp className="w-3 h-3 mr-1" data-id="element-134" />
              4%
            </div>
          </div>
          <div className="text-3xl font-bold text-amber-700 mb-1" data-id="element-135">
            {data.queueStuck}
          </div>
          <p className="text-xs text-amber-600/80" data-id="element-136">
            Currently stuck in round robin
          </p>
        </div>
      </Tile>

      {/* Missing Expected Campaign */}
      <Tile filterKey="missingCampaign" active={activeFilter === 'missingCampaign'} onSelect={onSelect} borderClass="border-rose-200" bgClass="bg-rose-50/30" ringClass="ring-rose-500 focus-visible:ring-rose-500" data-id="element-137">
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-138">
          <AlertCircle className="w-16 h-16 text-rose-600" data-id="element-139" />
        </div>
        <div className="relative z-10" data-id="element-140">
          <div className="flex items-center justify-between mb-2" data-id="element-141">
            <h3 className="text-sm font-semibold text-rose-800 uppercase tracking-wider" data-id="element-142">
              Missing Campaign
            </h3>
            <div className="flex items-center text-emerald-600 text-xs font-medium bg-emerald-50 px-2 py-1 rounded-full" data-id="element-143">
              <TrendingDown className="w-3 h-3 mr-1" data-id="element-144" />
              2%
            </div>
          </div>
          <div className="text-3xl font-bold text-rose-700 mb-1" data-id="element-145">
            {data.missingCampaign}
          </div>
          <p className="text-xs text-rose-600/80" data-id="element-146">
            Should be enrolled but missing
          </p>
        </div>
      </Tile>

      {/* Unlicensed States */}
      <Tile filterKey="unlicensed" active={activeFilter === 'unlicensed'} onSelect={onSelect} borderClass="border-violet-200" bgClass="bg-violet-50/30" ringClass="ring-violet-500 focus-visible:ring-violet-500" data-id="element-147">
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-148">
          <MapPinOff className="w-16 h-16 text-violet-600" data-id="element-149" />
        </div>
        <div className="relative z-10" data-id="element-150">
          <div className="flex items-center justify-between mb-2" data-id="element-151">
            <h3 className="text-sm font-semibold text-violet-800 uppercase tracking-wider" data-id="element-152">
              Unlicensed States
            </h3>
            <div className="flex items-center text-amber-600 text-xs font-medium bg-amber-50 px-2 py-1 rounded-full" data-id="element-153">
              <TrendingUp className="w-3 h-3 mr-1" data-id="element-154" />
              6%
            </div>
          </div>
          <div className="text-3xl font-bold text-violet-700 mb-1" data-id="element-155">
            {data.unlicensed}
          </div>
          <p className="text-xs text-violet-600/80" data-id="element-156">
            Property in a non-licensed state
          </p>
        </div>
      </Tile>

      {/* Enrolled */}
      <Tile filterKey="enrolled" active={activeFilter === 'enrolled'} onSelect={onSelect} borderClass="border-emerald-200" bgClass="bg-emerald-50/30" ringClass="ring-emerald-500 focus-visible:ring-emerald-500" data-id="element-157">
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-158">
          <CheckCircle2 className="w-16 h-16 text-emerald-600" data-id="element-159" />
        </div>
        <div className="relative z-10" data-id="element-160">
          <div className="flex items-center justify-between mb-2" data-id="element-161">
            <h3 className="text-sm font-semibold text-emerald-800 uppercase tracking-wider" data-id="element-162">
              Enrolled
            </h3>
            <div className="flex items-center text-emerald-600 text-xs font-medium bg-emerald-50 px-2 py-1 rounded-full" data-id="element-163">
              <TrendingUp className="w-3 h-3 mr-1" data-id="element-164" />
              8%
            </div>
          </div>
          <div className="text-3xl font-bold text-emerald-700 mb-1" data-id="element-165">
            {data.enrolled}
          </div>
          <p className="text-xs text-emerald-600/80" data-id="element-166">
            Active in campaign or drip
          </p>
        </div>
      </Tile>
    </div>;
}